import React, { useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { reportBusinessAsync } from '../../data-layer/business/actions';
import { reportPostAsync } from '../../data-layer/posts/actions';
import { useDispatch } from '../../data-layer/useDispatch';
import { Dialog, DialogContent, DialogTitle } from '../../ui/feedback/Dialog';
import { Button } from '../../ui/input/Button';
import { FormControlGroup } from '../../ui/input/FormControlGroup';
import { FormControlLabel, LabelPlacement } from '../../ui/input/FormControlLabel';
import { Radio } from '../../ui/input/Radio';
import { Box } from '../../ui/layout/Box';
import { Flex } from '../../ui/layout/Flex';
import { Typography } from '../../ui/typography/Typography';
import { useBackButton } from '../../utils/useBackButton';
import { useTranslation } from '../../utils/useTranslation';

export enum ReportReasons {
    OTHER = '0',
    VIOLENCE = '1',
    TRAGIC_CONTENT = '2',
    OFFENSIVE_REMARKS = '3',
    ADULT_CONTENT = '4',
    FRAUD = '5',
    SPAM_AND_ILLEGAL_CONTENT = '6',
    SELF_MEDICATION = '7',
    VIOLATION_OF_LAW = '8',
    CONTENT_INCONSISTENCY = '9',
}

interface FormValue {
    reason: ReportReasons;
}

export const ReportDialog = () => {
    const onNavigateBack = useBackButton();
    const t = useTranslation();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const reportTargetId = state.targetId;
    const targetType = state.targetType;

    const onReport = useCallback(
        (formValue: FormValue) => {
            if (targetType === 'post') {
                dispatch(reportPostAsync(reportTargetId, formValue.reason));
            }
            if (targetType === 'business') {
                dispatch(reportBusinessAsync(reportTargetId, formValue.reason));
            }
            onNavigateBack();
        },
        [dispatch, reportTargetId, onNavigateBack, targetType],
    );

    return (
        <Dialog
            onClose={onNavigateBack}
            borderRadius={{ xs: 0, sm: 3 }}
            maxWidth={{ xs: '100%', sm: 512 }}
        >
            <DialogTitle onClose={onNavigateBack}>
                <Typography variant="title3" weight="medium">
                    {t('Пожаловаться')}
                </Typography>
            </DialogTitle>
            <DialogContent py={0} px={2}>
                <Flex width="100%">
                    <Box py={2}>
                        <Typography variant="title2" weight="bold">
                            {t('Расскажите, что не так')}
                        </Typography>
                    </Box>

                    <Form
                        onSubmit={onReport}
                        initialValues={{}}
                        render={({ submitting, pristine, invalid, submitError, handleSubmit }) => (
                            <Flex as="form" width="100%" onSubmit={handleSubmit}>
                                <Field
                                    name="reason"
                                    required
                                    render={({ input }) => (
                                        <FormControlGroup width="100%" {...input}>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t(
                                                        'Контент не соответствует предложению на сайте',
                                                    )}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={
                                                                ReportReasons.CONTENT_INCONSISTENCY
                                                            }
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Нарушение закона')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={ReportReasons.VIOLATION_OF_LAW}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Насилие, шок-контент')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio value={ReportReasons.VIOLENCE} />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Трагичный контент')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={ReportReasons.TRAGIC_CONTENT}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Оскорбительные высказывания')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={ReportReasons.OFFENSIVE_REMARKS}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Контент 18+')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={ReportReasons.ADULT_CONTENT}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Спам или незаконный контент')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={
                                                                ReportReasons.SPAM_AND_ILLEGAL_CONTENT
                                                            }
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Умышленный обман')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={<Radio value={ReportReasons.FRAUD} />}
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Самолечение')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={
                                                        <Radio
                                                            value={ReportReasons.SELF_MEDICATION}
                                                        />
                                                    }
                                                />
                                            </Box>
                                            <Box
                                                width="100%"
                                                borderBottom="2px solid var(--divider)"
                                            >
                                                <FormControlLabel
                                                    label={t('Другое')}
                                                    labelPlacement={LabelPlacement.LEFT}
                                                    control={<Radio value={ReportReasons.OTHER} />}
                                                />
                                            </Box>
                                        </FormControlGroup>
                                    )}
                                />

                                <Flex width="100%" py={2}>
                                    <Box mt="auto" width="100%">
                                        {submitError && (
                                            <Typography variant="caption" color="error" mb={2}>
                                                {submitError}
                                            </Typography>
                                        )}
                                        <Button
                                            width="100%"
                                            type="submit"
                                            disabled={pristine || submitting || invalid}
                                        >
                                            {t('Отправить')}
                                        </Button>
                                    </Box>
                                </Flex>
                            </Flex>
                        )}
                    />
                </Flex>
            </DialogContent>
        </Dialog>
    );
};
